"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDefaultNodeServerMetricsService = exports.NodeServerMetricsService = void 0;
const client_cloudwatch_1 = require("@aws-sdk/client-cloudwatch");
const util_retry_1 = require("@aws-sdk/util-retry");
const constants_1 = require("../../../constants");
const isomorphic_1 = require("../../../isomorphic");
class NodeServerMetricsService {
    constructor(cloudwatchClientInstance, isOneBox = false, _maxEmitterThreshold = 3, _maxMetricDataCount = 1000, _metricsEmitterInterval = 3000) {
        this.METRIC_NAMESPACE = isOneBox ? 'MoneraAppNodeServer-2.0-OneBox' : 'MoneraAppNodeServer-2.0';
        this.MAX_EMITTER_THRESHOLD = _maxEmitterThreshold;
        this.MAX_METRIC_DATA_COUNT = _maxMetricDataCount > 1000 ? 1000 : _maxMetricDataCount;
        this.METRICS_EMITTER_INTERVAL = _metricsEmitterInterval;
        this.pendingMetricsStack = [];
        this.client = cloudwatchClientInstance;
        this.metricsEmitter = this.createMetricsEmitter();
        this.metricsEmitterFailedAttempts = 0;
        this.tenantId = constants_1.ENV_TENANT_ID || 'UNKNOWN_TENANT';
    }
    createMetricsEmitter() {
        return setInterval(() => {
            this.flushMetrics();
        }, this.METRICS_EMITTER_INTERVAL);
    }
    destroyMetricsEmitter() {
        clearInterval(this.metricsEmitter);
    }
    hardReset() {
        this.destroyMetricsEmitter();
        this.pendingMetricsStack = [];
        this.metricsEmitter = this.createMetricsEmitter();
    }
    flushMetrics() {
        return __awaiter(this, void 0, void 0, function* () {
            const clientPromises = [];
            while (this.pendingMetricsStack.length > 0) {
                const pendingMetrics = this.pendingMetricsStack.pop();
                if (pendingMetrics === undefined || pendingMetrics.length === 0) {
                    continue;
                }
                const input = {
                    Namespace: this.METRIC_NAMESPACE,
                    MetricData: pendingMetrics
                };
                const command = new client_cloudwatch_1.PutMetricDataCommand(input);
                const clientPromise = this.client
                    .send(command)
                    .then(() => {
                    if (this.metricsEmitterFailedAttempts > 0) {
                        this.metricsEmitterFailedAttempts = 0;
                    }
                })
                    .catch((err) => {
                    isomorphic_1.LOGGER.error(`Cloudwatch metrics data emission failed:\n${err}`);
                    this.metricsEmitterErrorHandler(pendingMetrics);
                });
                clientPromises.push(clientPromise);
            }
            Promise.all(clientPromises);
        });
    }
    metricsEmitterErrorHandler(pendingMetrics) {
        this.metricsEmitterFailedAttempts += 1;
        const isAtMaxAllowedEmitterThreshold = this.metricsEmitterFailedAttempts >= this.MAX_EMITTER_THRESHOLD;
        if (isAtMaxAllowedEmitterThreshold) {
            isomorphic_1.LOGGER.error('Reached max threshold of failed attempts to emit metrics data to cloudwatch. Clearing out metrics data cache');
            this.pendingMetricsStack = [];
            this.metricsEmitterFailedAttempts = 0;
            isomorphic_1.LOGGER.error('Metrics data cache cleared');
        }
        else {
            this.pendingMetricsStack.push(pendingMetrics);
        }
    }
    recordMetricsData(event) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const tokenizedMetric = this.tokenizeMetric(event);
            if (!tokenizedMetric.HasTenantId || tokenizedMetric.IsALL) {
                let latestBatch = (_a = this.pendingMetricsStack.pop()) !== null && _a !== void 0 ? _a : [];
                const cloudwatchMetricsDataMap = this.getCloudwatchMetricsDataMap(tokenizedMetric);
                while (cloudwatchMetricsDataMap.length > 0) {
                    if (latestBatch.length < this.MAX_METRIC_DATA_COUNT) {
                        latestBatch.push(cloudwatchMetricsDataMap.pop());
                    }
                    else {
                        this.pendingMetricsStack.push(latestBatch);
                        latestBatch = [cloudwatchMetricsDataMap.pop()];
                    }
                }
                this.pendingMetricsStack.push(latestBatch);
            }
        });
    }
    getCloudwatchMetricsDataMap(event) {
        const map = [];
        const dimensions = [];
        const TIMESTAMP_NOW = new Date();
        dimensions.push({
            Name: 'IsInternal',
            Value: String(event.IsInternal)
        });
        map.push({
            MetricName: event.cleanedMetric.eventName,
            Dimensions: [...dimensions],
            Timestamp: TIMESTAMP_NOW,
            Value: event.cleanedMetric.value,
            Unit: event.cleanedMetric.unit
        });
        dimensions.push({
            Name: 'tenantId',
            Value: String(event.tenantId)
        });
        map.push({
            MetricName: event.cleanedMetric.eventName,
            Dimensions: [...dimensions],
            Timestamp: TIMESTAMP_NOW,
            Value: event.cleanedMetric.value,
            Unit: event.cleanedMetric.unit
        });
        dimensions.push({
            Name: 'IsRobot',
            Value: String(event.IsRobot)
        });
        map.push({
            MetricName: event.cleanedMetric.eventName,
            Dimensions: [...dimensions],
            Timestamp: TIMESTAMP_NOW,
            Value: event.cleanedMetric.value,
            Unit: event.cleanedMetric.unit
        });
        return map;
    }
    tokenizeMetric(event) {
        let eventString = event.eventName;
        let isRobot = false, isALL = false, isInternal = false, hasTenantId = false;
        if (eventString.startsWith(`${constants_1.ROBOT}:`)) {
            eventString = eventString.slice(constants_1.ROBOT.length + 1);
            isRobot = true;
        }
        if (eventString.startsWith(`${constants_1.INTERNAL}:`)) {
            eventString = eventString.slice(constants_1.INTERNAL.length + 1);
            isInternal = true;
        }
        if (eventString.startsWith(`${constants_1.ALL}:`)) {
            eventString = eventString.slice(constants_1.ALL.length + 1);
            isALL = true;
            event.eventName = eventString.toString();
        }
        if (eventString.startsWith(this.tenantId)) {
            eventString = eventString.slice(this.tenantId.length + 1);
            hasTenantId = true;
            event.eventName = eventString.toString();
        }
        return {
            cleanedMetric: event,
            IsRobot: isRobot,
            IsInternal: isInternal,
            IsALL: isALL,
            HasTenantId: hasTenantId,
            tenantId: this.tenantId
        };
    }
    recordCounter(eventObject) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.recordMetricsData(eventObject);
        });
    }
    recordTimer(eventObject) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.recordMetricsData(eventObject);
        });
    }
}
exports.NodeServerMetricsService = NodeServerMetricsService;
const getDefaultNodeServerMetricsService = (isOneBox = false) => new NodeServerMetricsService(new client_cloudwatch_1.CloudWatchClient({
    region: constants_1.ENV_REGION,
    retryStrategy: new util_retry_1.AdaptiveRetryStrategy(() => __awaiter(void 0, void 0, void 0, function* () { return Promise.resolve(constants_1.CLOUDWATCH_RETRY_MAX_ATTEMPTS); }))
}), isOneBox);
exports.getDefaultNodeServerMetricsService = getDefaultNodeServerMetricsService;
